import en from "./locales/en";
import { LANGS, LANG_NAMES, type Dict, type Lang } from "./types";

export { LANGS, LANG_NAMES };
export type { Dict, Lang };

/**
 * Английский вшит статически: он же запасной вариант, и без него `t()` не мог
 * бы вернуть ничего до загрузки. Остальные 19 приезжают динамическим импортом,
 * поэтому в бандл попадает только выбранный язык, а не все двадцать.
 */
const loaders: Record<Lang, () => Promise<{ default: Dict }>> = {
  en: () => Promise.resolve({ default: en }),
  ru: () => import("./locales/ru"),
  de: () => import("./locales/de"),
  fr: () => import("./locales/fr"),
  es: () => import("./locales/es"),
  pt: () => import("./locales/pt"),
  it: () => import("./locales/it"),
  nl: () => import("./locales/nl"),
  pl: () => import("./locales/pl"),
  cs: () => import("./locales/cs"),
  ro: () => import("./locales/ro"),
  uk: () => import("./locales/uk"),
  tr: () => import("./locales/tr"),
  el: () => import("./locales/el"),
  sv: () => import("./locales/sv"),
  id: () => import("./locales/id"),
  hi: () => import("./locales/hi"),
  ja: () => import("./locales/ja"),
  ko: () => import("./locales/ko"),
  zh: () => import("./locales/zh"),
};

const loaded = new Map<Lang, Dict>([["en", en]]);

export async function loadDict(l: Lang): Promise<Dict> {
  const have = loaded.get(l);
  if (have) return have;
  try {
    const m = await loaders[l]();
    // Ключ за ключом поверх английского: если в переводе что-то забыли или
    // добавили новую строку и не перевели, на её месте будет английский текст,
    // а не пустота. Проверка полноты есть в тестах, но она не должна быть
    // единственным, что стоит между пользователем и дырой в интерфейсе.
    const merged = { ...en, ...m.default };
    loaded.set(l, merged);
    return merged;
  } catch {
    return en;   // язык не приехал — интерфейс остаётся на английском
  }
}

/** Словарь языка. До загрузки отдаёт английский, чтобы ничего не падало. */
export function t(l: Lang): Dict {
  return loaded.get(l) ?? en;
}

const isLang = (v: string): v is Lang => (LANGS as readonly string[]).includes(v);

/**
 * Язык берётся из настроек устройства и браузера: navigator.languages —
 * это и есть системный список предпочтений. Сохранённый выбор важнее.
 * `pt-BR` и `zh-Hans` сводятся к базовому коду.
 */
export function detectLang(): Lang {
  try {
    const stored = localStorage.getItem("pw.lang");
    if (stored && isLang(stored)) return stored;
  } catch { /* приватный режим */ }

  const prefs = navigator.languages?.length ? navigator.languages : [navigator.language || "en"];
  for (const p of prefs) {
    const base = p.toLowerCase().split("-")[0] ?? "";
    if (isLang(base)) return base;
    // Языки, которые встречаются как отдельные коды, но нам ближе соседний.
    if (base === "be" || base === "kk") return "ru";
    if (base === "nb" || base === "nn" || base === "da") return "sv";
    if (base === "ms") return "id";
  }
  return "en";   // ничего не подошло — английский
}

export function setLang(l: Lang) {
  try { localStorage.setItem("pw.lang", l); } catch { /* приватный режим */ }
}

/** Подстановка {v} в шаблон. Проще, чем полноценный формат, и этого хватает. */
export const fmt = (template: string, v: string | number) =>
  template.replace("{v}", String(v));

/** Формулировка сигнала на выбранном языке. */
export function evidenceText(s: { evKey: string; evValue: string }, lang: Lang): string {
  const d = t(lang) as unknown as Record<string, string>;
  const tpl = d[s.evKey];
  return tpl ? fmt(tpl, s.evValue) : s.evKey;
}

/** Ограничение профиля словами: speed:low, vocal:instrumental, acoustic:acoustic */
export function constraintText(c: string, lang: Lang): string {
  const d = t(lang) as unknown as Record<string, string>;
  return d[c] ?? c.split(":")[1] ?? c;
}
